/**
 * InputArea.js - Chat Input Component
 * Handles message input, file uploads, and model selection
 */

import React from 'react';
import { Icons } from './Icons';
import { config } from '../config';
import { logger } from '../logger';

function InputArea({
  input,
  setInput,
  isLoading,
  selectedImage,
  setSelectedImage,
  selectedPdf,
  setSelectedPdf,
  selectedPdfName,
  setSelectedPdfName,
  selectedExcel,
  setSelectedExcel,
  selectedExcelName,
  setSelectedExcelName,
  selectedVideo,
  setSelectedVideo,
  selectedVideoName,
  setSelectedVideoName,
  selectedModel,
  setSelectedModel,
  isMultimodal,
  isUploadDropdownOpen,
  setIsUploadDropdownOpen,
  isModelDropdownOpen,
  setIsModelDropdownOpen,
  onSubmit,
  onStop,
  isAsyncJobLoading,
  onKeyDown,
  imageInputRef,
  pdfInputRef,
  excelInputRef,
  videoInputRef,
  uploadDropdownRef,
  modelDropdownRef,
  textareaRef,
  onImageChange,
  onPdfChange,
  onExcelChange,
  onVideoChange,
  onToolsHelpOpen,
}) {
  return (
    <div className="input-area">
      <div className="input-container">
        {selectedImage && (
          <div className="image-preview-container">
            <img 
              src={`data:image/jpeg;base64,${selectedImage}`} 
              alt="Selected" 
              className="image-preview" 
            />
            <button 
              type="button" 
              className="clear-image-btn" 
              onClick={() => {
                setSelectedImage(null);
                if (imageInputRef.current) imageInputRef.current.value = '';
              }}
            >
              ×
            </button>
          </div>
        )}
        
        {selectedPdf && (
          <div className="pdf-preview-container">
            <Icons.Pdf />
            <span className="pdf-preview-name">{selectedPdfName}</span>
            <button 
              type="button" 
              className="clear-pdf-btn" 
              onClick={() => {
                setSelectedPdf(null);
                setSelectedPdfName(null);
                if (pdfInputRef.current) pdfInputRef.current.value = '';
              }}
            >
              ×
            </button>
          </div>
        )}
        
        {selectedExcel && (
          <div className="excel-preview-container">
            <Icons.Excel />
            <span className="excel-preview-name">{selectedExcelName}</span>
            <button 
              type="button" 
              className="clear-excel-btn" 
              onClick={() => {
                setSelectedExcel(null);
                setSelectedExcelName(null);
                if (excelInputRef.current) excelInputRef.current.value = '';
              }}
            >
              ×
            </button>
          </div>
        )}

        {selectedVideo && (
          <div className="excel-preview-container">
            <Icons.Video />
            <span className="excel-preview-name">{selectedVideoName}</span>
            <button
              type="button"
              className="clear-excel-btn"
              onClick={() => {
                setSelectedVideo(null);
                setSelectedVideoName(null);
                if (videoInputRef.current) videoInputRef.current.value = '';
              }}
            >
              ×
            </button>
          </div>
        )}
        
        <form onSubmit={onSubmit} className="input-form">
          {/* Hidden file inputs */}
          <input
            type="file"
            accept="image/*"
            ref={imageInputRef}
            style={{ display: 'none' }}
            onChange={onImageChange}
          />
          <input
            type="file"
            accept="application/pdf"
            ref={pdfInputRef}
            style={{ display: 'none' }}
            onChange={onPdfChange}
          />
          <input
            type="file"
            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            ref={excelInputRef}
            style={{ display: 'none' }}
            onChange={onExcelChange}
          />
          <input
            type="file"
            accept={config.videoAllowedFormats.join(',')}
            ref={videoInputRef}
            style={{ display: 'none' }}
            onChange={onVideoChange}
          />
          
          {/* Upload Plus Button and Dropdown */}
          <div className="upload-dropdown-container" ref={uploadDropdownRef}>
            <button 
              type="button" 
              className={`upload-plus-btn ${(selectedImage || selectedPdf || selectedExcel || selectedVideo) ? 'has-files' : ''}`}
              onClick={() => setIsUploadDropdownOpen(!isUploadDropdownOpen)} 
              disabled={isLoading}
              title="Attach files"
            >
              <Icons.Plus />
            </button>
            
            {isUploadDropdownOpen && (
              <div className="upload-dropdown">
                <div 
                  className={`upload-option ${!isMultimodal ? 'disabled' : ''}`}
                  onClick={() => {
                    if (isMultimodal) {
                      imageInputRef.current?.click();
                      setIsUploadDropdownOpen(false);
                    }
                  }}
                >
                  <Icons.Image />
                  <span>Image</span>
                  {!isMultimodal && <span className="upload-note">Requires multimodal model</span>}
                </div>
                <div 
                  className="upload-option"
                  onClick={() => {
                    pdfInputRef.current?.click();
                    setIsUploadDropdownOpen(false);
                  }}
                >
                  <Icons.Pdf />
                  <span>PDF</span>
                </div>
                <div 
                  className="upload-option"
                  onClick={() => {
                    excelInputRef.current?.click();
                    setIsUploadDropdownOpen(false);
                  }}
                >
                  <Icons.Excel />
                  <span>Excel</span>
                </div>
                <div
                  className="upload-option"
                  onClick={() => {
                    videoInputRef.current?.click();
                    setIsUploadDropdownOpen(false);
                  }}
                >
                  <Icons.Video />
                  <span>Video</span>
                </div>
              </div>
            )}
          </div>
          
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Message..."
            disabled={isLoading}
            autoFocus
            rows={1}
          />
          
          {/* Tools Help Button */}
          <button
            type="button"
            className="tools-help-btn"
            onClick={onToolsHelpOpen}
            title="Show available tools"
            aria-label="Show available tools"
          >
            ?
          </button>

          {/* Model Selector Dropdown */}
          <div className="model-dropdown-container" ref={modelDropdownRef}>
            <button 
              type="button" 
              className="model-btn"
              onClick={() => setIsModelDropdownOpen(!isModelDropdownOpen)} 
              disabled={isLoading}
              title="Choose model"
            >
              <span>
                {(() => {
                  const model = config.models.find(m => m.id === selectedModel);
                  return model ? `${model.name}${model.multimodal ? ' (Multi)' : ''}` : 'Model';
                })()}
              </span>
              <Icons.ChevronDown />
            </button>
            
            {isModelDropdownOpen && (
              <div className="model-dropdown">
                <div className="model-dropdown-header">Choose your model</div>
                {config.models.map(model => (
                  <div 
                    key={model.id}
                    className={`model-option ${selectedModel === model.id ? 'selected' : ''}`}
                    onClick={() => {
                      setSelectedModel(model.id);
                      setIsModelDropdownOpen(false);
                      // Clear image if switching to non-multimodal model
                      if (!model.multimodal) {
                        setSelectedImage(null);
                      }
                      logger.userAction('Change model', { model: model.id });
                    }}
                  >
                    <span className="model-option-name">
                      {model.name}{model.multimodal ? ' (Multi)' : ''}
                    </span>
                    {selectedModel === model.id && (
                      <Icons.Check />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {(isLoading || isAsyncJobLoading) ? (
            <button
              type="button"
              className="stop-btn"
              onClick={onStop}
              title="Stop generation"
            >
              <Icons.Stop />
            </button>
          ) : (
            <button
              type="submit"
              className="send-btn"
              disabled={!input.trim() && !selectedImage && !selectedPdf && !selectedExcel && !selectedVideo}
              title="Send message"
            >
              <Icons.Send />
            </button>
          )}
        </form>
        
        <p className="input-disclaimer">
          AI can make mistakes. Consider checking important information.
        </p>
      </div>
    </div>
  );
}

export default InputArea;

